import { FC, HTMLAttributes, useState, useCallback } from "react";
import styles from "./ReactionCard.module.scss";
import { ApplicationUserDto } from "@entities/commonTypes";
import {
  useExtractPhotoColors,
  useGetUserAge,
  useGetUserAvatar,
  useGetUserName,
} from "@shared/hooks";
import { COUNTRY_NAME, REACTIONS_TYPES } from "@shared/constants";
import chatIcon from "/svg/fi-rr-comments.svg";
import {
  Avatar,
  Rating,
  useLazyGetApiChatByIdQuery,
  UserRating,
} from "@shared/index";
import { Link } from "react-router-dom";
import { useDecodeHtml } from "@shared/utils/decodeHTML";

// Constants
const CHAT_FETCH_TIMEOUT_MS = 1000;

interface ReactionCardProps extends HTMLAttributes<HTMLDivElement> {
  user: ApplicationUserDto;
  reactionType: REACTIONS_TYPES;
}

export const ReactionCard: FC<ReactionCardProps> = ({
  user,
  reactionType,
  style,
  className,
}) => {
  const [fetchChat] = useLazyGetApiChatByIdQuery();
  const [hasChatLink, setHasChatLink] = useState<boolean | null>(null);

  const fetchChatWithTimeout = useCallback(
    async (userId: string): Promise<string | null> => {
      try {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("Chat fetch timeout")),
            CHAT_FETCH_TIMEOUT_MS
          )
        );

        const chatResponse = await Promise.race([
          fetchChat({ id: userId }),
          timeoutPromise,
        ]);

        return chatResponse.data?.data?.link || null;
      } catch (error) {
        console.error("Error fetching chat link:", error);
        return null;
      }
    },
    [fetchChat]
  );

  const handleGetChat = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (reactionType !== REACTIONS_TYPES.Matches) return;

    try {
      const chatLink = await fetchChatWithTimeout(user.id as string);

      if (chatLink) {
        setHasChatLink(true);
        window.Telegram.WebApp.openTelegramLink(chatLink);
      } else {
        setHasChatLink(false);
      }
    } catch (error) {
      console.error("Failed to fetch chat:", error);
      setHasChatLink(false);
    }
  };
  const { dominantColors, isLoading } = useExtractPhotoColors(
    useGetUserAvatar(user.photos, true)
  );

  return (
    <Link
      to={`/users/${user.id as string}`}
      state={{ activeTab: reactionType }}
    >
      <div
        className={`${className && className} && ${styles.wrapper}`}
        style={style}
      >
        <div className={styles.userAvatarContainer}>
          {!isLoading && dominantColors.length > 0 ? (
            <Avatar
              key={useGetUserAvatar(user.photos, true)}
              src={useGetUserAvatar(user.photos, true)}
              dominantColors={dominantColors}
              showPulseEffect={true}
            />
          ) : (
            <Avatar
              key={useGetUserAvatar(user.photos, true)}
              src={useGetUserAvatar(user.photos, true)}
            />
          )}
          <Rating
            isReactions={true}
            badges={user.badges || []}
            userId={user.id}
          />
          {user.social_rating && (
            <UserRating isReactions={true} userRating={user.social_rating} />
          )}
        </div>

        <div className={styles.userInfo}>
          <p className={styles.userName}>
            {useDecodeHtml(useGetUserName(user.name, false))}
            {user.birth_date && `, ${useGetUserAge(user.birth_date)}`}
          </p>
          <p className={styles.userCity}>
            {COUNTRY_NAME}
            {user.city && `, ${useDecodeHtml(user.city)}`}
          </p>
          {user.job_title && (
            <div className={styles.userJobTitle}>
              {useDecodeHtml(user.job_title)}
            </div>
          )}
        </div>
        <button
          className={`${styles.getChatLinkBtn} ${
            (reactionType !== REACTIONS_TYPES.Matches ||
              hasChatLink === false) &&
            styles.invisibleBtn
          }`}
          onClick={(e) => handleGetChat(e)}
        >
          <img src={chatIcon} className={styles.getChatLinkIcon} />
        </button>
      </div>
    </Link>
  );
};
